/**
 * Portfolio + equity curve panel (§A.6). `equity_curve.json` is the one
 * panel in this app backed entirely by the *production* contract today
 * (`organism.observatory.publish.publish_snapshot`) — no OBS-2/6/7/8
 * extension needed, so this is the first genuinely-real-data panel.
 *
 * design/revamp/DIRECTION.md §1.7, updated per orchestrator course
 * correction (2026-09-28): chart + positions/orders now lay out side by
 * side (`.obs-portfolio-columns`) instead of stacked, so the whole section
 * is visible without excess scrolling. The NAV/unrealized/realized P&L
 * rows that used to live here are gone — the vitals hero above now shows
 * all three, and repeating them here was pure duplication; this panel
 * keeps only the fields the hero doesn't have (available cash, positions
 * value, drawdown, growth score, fees, tariff).
 */

import type { RoleSnapshot } from "../data/snapshotIndex";
import type { LogEvent } from "../types/events";
import type { EquityCurveRow } from "../types/snapshot";
import type { PerformanceDocument } from "../types/incremental";
// Type-only: `EquityChart` pulls in `lightweight-charts` (§B.6's budgeted-
// first-paint concern, same reasoning as `OrganismScreen.ts`'s dynamic
// `BrainPanel` import) — `renderChartColumn` below `await import()`s the
// real module so Rollup code-splits it into its own chunk instead of the
// main one (2026-09-28 bundle-size fix).
import type { EquityChart } from "../charts/EquityChart";
import { renderNotPublishedYet } from "./NotPublishedYet";
import { attachTooltip } from "../ui/tooltip";
import { statLabel } from "../ui/statLabel";
import {
  formatMagnitudePercent,
  formatMoney,
  formatPrice,
  formatQuantity,
  formatSignedMoney,
  formatSignedNumber,
  toneClass,
} from "../format/number";
import { buildPositionRows, latestOpenOrdersCount, type PositionRow } from "./portfolioPositions";
import { Poller } from "../data/polling";
import { isNotPublishedYet } from "../data/fetchJson";
import { config } from "../config";
import { latestByKind } from "../brain/sessionStatus";

interface PortfolioSnapshot {
  readonly curve: readonly EquityCurveRow[];
  readonly performance: PerformanceDocument | null;
  readonly events: readonly LogEvent[];
}

type RowFormat =
  | { readonly kind: "money"; readonly value: string | null }
  | { readonly kind: "signed-money"; readonly value: string | null }
  | { readonly kind: "magnitude-percent"; readonly value: number | null }
  | { readonly kind: "signed-number"; readonly value: number | null; readonly decimals?: number }
  | { readonly kind: "text"; readonly value: string };

interface StatRow {
  readonly plainLabel: string;
  readonly technicalLabel?: string;
  readonly tooltip: string;
  readonly format: RowFormat;
}

export class PortfolioPanel {
  private chart: EquityChart | null = null;
  /** Guards against the panel being disposed while the code-split
   * `EquityChart`/lightweight-charts chunk is still in flight (code-review
   * finding): `App.showRoute` disposes the outgoing screen synchronously
   * on every navigation, regardless of whether that screen's own mount()
   * has resolved — navigate away before `chartModulePromise` settles and,
   * without this flag, `dispose()` runs while `this.chart` is still null
   * (a no-op), then the import resolves afterwards and constructs a live
   * chart (with its own ResizeObserver) into a container the screen has
   * already torn down, which nothing ever disposes again. */
  private disposed = false;
  /** OQA: this panel used to render ONCE — the positions table, cash, drawdown,
   * fees and the NAV chart froze at page load (a fill minutes later never showed).
   * It now polls on the shared cadence and patches these in place. */
  private poller: Poller<PortfolioSnapshot> | null = null;
  private statsHost: HTMLDivElement | null = null;
  private positionsHost: HTMLDivElement | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly role: RoleSnapshot,
  ) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";
    // Both kicked off now, genuinely in parallel (a prior version started
    // the chart import only after `await this.role.equityCurve()` had
    // already resolved, despite its own comment claiming otherwise —
    // code-review finding): lightweight-charts is code-split out of the
    // main bundle (2026-09-28 bundle-size fix), so its network fetch
    // should overlap the equity-curve round trip, not follow it. Brain/
    // Log/VitalsHero all call `role.events()` at (or near) their own
    // mount()'s start too, and `RoleSnapshot.events()` only coalesces
    // concurrent in-flight callers (snapshotIndex.ts's `refreshInFlight`
    // guard); firing this one late meant it almost always missed that
    // window and re-fetched independently (code-review finding, revamp
    // self-review round 1). The curve.length === 0 early-return below
    // never touches either promise, so a real rejection on that path must
    // still be observed here (not left as an unhandled rejection) —
    // swallowed, since neither the chart nor the positions table renders
    // when the equity curve itself is empty.
    const eventsPromise = this.role.events().catch(() => [] as readonly LogEvent[]);
    const chartModulePromise = import("../charts/EquityChart");
    // Observed unconditionally, right away (code-review finding): a
    // `.catch()` attached only on the `curve.length === 0` path below never
    // runs when `equityCurve()` itself *rejects* (a real error — the empty-
    // array case is a legitimate non-error result, not a rejection) —
    // control never reaches that branch, so the chart import's own
    // rejection (if it also fails) would be truly unhandled alongside the
    // propagated error. This no-op catch only marks the promise "observed"
    // for that warning; the real value/error is still awaited properly by
    // `renderChartColumn` below on the success path.
    chartModulePromise.catch(() => {});
    const curve = await this.role.equityCurve();
    if (curve.length === 0) {
      this.container.appendChild(
        renderNotPublishedYet(
          "Equity curve",
          "No portfolio.snapshot cycles have run for this role yet.",
        ),
      );
      // A fresh episode's first cycle: re-mount the full panel once it lands.
      this.startPoller(true);
      return;
    }

    const columns = document.createElement("div");
    columns.className = "obs-portfolio-columns";

    const left = document.createElement("div");
    left.className = "obs-portfolio-col";
    left.appendChild(await this.renderChartColumn(curve, chartModulePromise));
    const performance = await this.role.performance();
    this.statsHost = document.createElement("div");
    this.statsHost.appendChild(this.renderStatRows(curve, performance));
    left.appendChild(this.statsHost);

    const right = document.createElement("div");
    right.className = "obs-portfolio-col";
    const events = await eventsPromise;
    this.positionsHost = document.createElement("div");
    this.positionsHost.appendChild(this.renderPositionsTable(events));
    this.positionsHost.appendChild(this.renderOpenOrdersSection(events));
    right.appendChild(this.positionsHost);

    columns.appendChild(left);
    columns.appendChild(right);
    this.container.appendChild(columns);
    if (!this.disposed) this.startPoller(false);
  }

  private startPoller(remountWhenCurveAppears: boolean): void {
    this.poller?.stop();
    this.poller = new Poller<PortfolioSnapshot>(
      async () => {
        const [curve, performance, events] = await Promise.all([
          this.role.equityCurve(),
          this.role.performance(),
          this.role.events(),
        ]);
        const snapshot = latestByKind(events, "portfolio.snapshot");
        const key =
          `${curve.length}:${curve[curve.length - 1]?.seq ?? "none"}:` +
          `${performance?.as_of_seq ?? "none"}:${snapshot?.seq ?? "none"}`;
        return { key, value: { curve, performance, events } };
      },
      (result) => {
        if (this.disposed || !result.value || result.status === "unchanged") return;
        const { curve, performance, events } = result.value;
        if (remountWhenCurveAppears) {
          if (curve.length > 0) {
            this.poller?.stop();
            void this.mount();
          }
          return;
        }
        if (curve.length > 0) {
          this.chart?.setEquityCurve(curve);
          this.statsHost?.replaceChildren(this.renderStatRows(curve, performance));
        }
        this.positionsHost?.replaceChildren(
          this.renderPositionsTable(events),
          this.renderOpenOrdersSection(events),
        );
      },
      { intervalMs: config.pollIntervalMs, backoffMaxMs: config.pollBackoffMaxMs },
      isNotPublishedYet,
    );
    this.poller.start();
  }

  private async renderChartColumn(
    curve: readonly EquityCurveRow[],
    chartModulePromise: Promise<typeof import("../charts/EquityChart")>,
  ): Promise<HTMLDivElement> {
    const wrap = document.createElement("div");

    // A small fixed-DOM legend above the chart — design/revamp/DIRECTION.md
    // §1.7 fix for the reported "NAV label covers the axis tick" bug: a
    // label drawn in the chart's own coordinate space can float onto a
    // tick; a label rendered in normal document flow above it never can.
    const legend = document.createElement("div");
    legend.className = "obs-equity-legend";
    const swatch = document.createElement("span");
    swatch.className = "obs-equity-legend-swatch";
    legend.appendChild(swatch);
    legend.appendChild(document.createTextNode("NAV over time"));
    wrap.appendChild(legend);

    // A fixed px height, not a percentage — see EquityChart's own note on
    // the ResizeObserver runaway-growth bug this avoids.
    const chartWrap = document.createElement("div");
    chartWrap.className = "obs-equity-chart-wrap";
    wrap.appendChild(chartWrap);
    const { EquityChart } = await chartModulePromise;
    if (this.disposed) return wrap; // torn down while the chunk was in flight — never construct
    this.chart = new EquityChart(chartWrap);
    this.chart.setEquityCurve(curve);
    return wrap;
  }

  private renderStatRows(
    curve: readonly EquityCurveRow[],
    performance: PerformanceDocument | null,
  ): HTMLDivElement {
    const latest = curve[curve.length - 1]!;
    // NAV / unrealized / realized P&L are deliberately NOT repeated here —
    // the vitals hero above already shows all three; this panel keeps only
    // what the hero doesn't (orchestrator course correction, 2026-09-28).
    const rows: StatRow[] = [
      {
        plainLabel: "Available cash",
        technicalLabel: "trading cash",
        tooltip: "Unencumbered cash available for new orders.",
        format: { kind: "money", value: latest.trading_cash },
      },
      {
        plainLabel: "Positions value",
        tooltip: "Current mark-to-market value of all open positions.",
        format: { kind: "money", value: latest.positions_value },
      },
      {
        plainLabel: "As of",
        tooltip: "Event time of the equity curve's most recent row.",
        format: { kind: "text", value: latest.event_time },
      },
    ];

    if (performance) {
      rows.push(
        {
          plainLabel: "Drawdown",
          technicalLabel: "from peak NAV",
          tooltip:
            "Current drawdown from the running peak NAV — a non-negative magnitude (0 = at the peak); shown in red whenever it's above zero, never as a gain.",
          format: { kind: "magnitude-percent", value: performance.drawdown },
        },
        {
          plainLabel: "Growth score",
          technicalLabel: "reward, log growth",
          tooltip:
            "The organism's own optimization target: transfer-neutral net resource log growth (a cumulative log-return, not a percentage).",
          format: { kind: "signed-number", value: performance.reward_log_growth, decimals: 6 },
        },
        {
          plainLabel: "Fees paid",
          technicalLabel: "cumulative, broker",
          tooltip: "Cumulative broker fees paid.",
          format: { kind: "money", value: performance.fees_broker_cum },
        },
        {
          plainLabel: "Compute/data cost",
          technicalLabel: "tariff, cumulative",
          tooltip: "Cumulative posted tariff (compute/data cost) charged against this organism.",
          format: { kind: "money", value: performance.tariff_cum },
        },
      );
    }

    const stats = document.createElement("div");
    for (const row of rows) {
      stats.appendChild(this.renderRow(row));
    }
    return stats;
  }

  private renderPositionsTable(events: readonly LogEvent[]): HTMLElement {
    const wrap = document.createElement("div");
    const heading = document.createElement("div");
    heading.className = "obs-section-heading";
    heading.textContent = "Positions";
    wrap.appendChild(heading);

    const positions = buildPositionRows(events);
    if (positions.length === 0) {
      wrap.appendChild(
        renderNotPublishedYet(
          "Positions",
          "Flat — no open positions in the latest portfolio.snapshot.",
          "flat",
        ),
      );
      return wrap;
    }

    const table = document.createElement("table");
    table.className = "obs-table";
    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    for (const [label, tip] of [
      ["Symbol", "The position's ticker."],
      ["Qty", "Quantity held."],
      ["Avg cost", "Average cost per unit: the position's total cost basis ÷ quantity."],
      ["Mark", "Current mark price per unit — stale marks are flagged."],
      [
        "Unrealized P&L",
        "mark × quantity − total cost basis, computed from this row's own published fields.",
      ],
    ] as const) {
      const th = document.createElement("th");
      th.textContent = label;
      attachTooltip(th, tip);
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    for (const position of positions) {
      tbody.appendChild(this.renderPositionRow(position));
    }
    table.appendChild(tbody);
    wrap.appendChild(table);
    return wrap;
  }

  private renderPositionRow(position: PositionRow): HTMLTableRowElement {
    const row = document.createElement("tr");
    const symbolCell = document.createElement("td");
    symbolCell.textContent = position.symbol;
    row.appendChild(symbolCell);

    const qtyCell = document.createElement("td");
    // Never formatCount: quantity is a genuine fractional Decimal (crypto
    // settles on the 1e-9 grid) — Math.trunc would misrepresent a real
    // fractional holding (code-review finding, revamp round 2). OQA: the published Decimal string verbatim, trailing zeros trimmed — whole
    // shares read "21" (not "21.0000"), fractional crypto keeps its 1e-9 digits.
    qtyCell.textContent = formatQuantity(position.quantityText ?? position.quantity);
    row.appendChild(qtyCell);

    const basisCell = document.createElement("td");
    basisCell.textContent = formatPrice(position.avgCost, { currency: true });
    row.appendChild(basisCell);

    const markCell = document.createElement("td");
    markCell.textContent = formatPrice(position.markText ?? position.markPrice, {
      currency: true,
    });
    if (position.markStale) {
      markCell.classList.add("obs-value-down");
      attachTooltip(markCell, "This mark is flagged stale (mark_stale) by the snapshot itself.");
    }
    row.appendChild(markCell);

    const pnlCell = document.createElement("td");
    const pnl = formatSignedMoney(position.unrealizedPnl);
    pnlCell.textContent = pnl.text;
    pnlCell.classList.add(toneClass(pnl.tone));
    row.appendChild(pnlCell);

    return row;
  }

  /** No per-order detail (symbol/side/limit price) exists in this app's
   * event contract yet — only the aggregate count. Rather than fabricate
   * rows, this renders the honest count with a note, the same "state what's
   * missing, never guess" rule `MarketPanel.ts` uses for a ladder-less
   * feed. */
  private renderOpenOrdersSection(events: readonly LogEvent[]): HTMLElement {
    const wrap = document.createElement("div");
    const heading = document.createElement("div");
    heading.className = "obs-section-heading";
    heading.textContent = "Open orders";
    wrap.appendChild(heading);

    const count = latestOpenOrdersCount(events);
    const row = document.createElement("div");
    row.className = "obs-stat-row";
    const label = statLabel("Open orders");
    const value = document.createElement("span");
    value.textContent = count !== null ? String(count) : "–";
    row.appendChild(label);
    row.appendChild(value);
    wrap.appendChild(row);

    const note = document.createElement("div");
    note.className = "obs-panel-hint";
    // `count !== null && count > 0` (not the old truthy `count && ...`,
    // which treated "unknown" and "a real zero" identically and printed the
    // fabricated claim "No open orders" even when the count genuinely
    // couldn't be read — code-review finding, revamp self-review round 2).
    note.textContent =
      count === null
        ? "Open order count isn't available from the latest portfolio.snapshot."
        : count > 0
          ? "Per-order detail (symbol, side, limit price) isn't published yet — count only."
          : "No open orders in the latest portfolio.snapshot.";
    wrap.appendChild(note);
    return wrap;
  }

  private renderRow(row: StatRow): HTMLDivElement {
    const rowEl = document.createElement("div");
    rowEl.className = "obs-stat-row";
    const labelEl = statLabel(row.plainLabel, row.technicalLabel);
    attachTooltip(labelEl, row.tooltip);
    const valueEl = document.createElement("span");

    switch (row.format.kind) {
      case "money":
        valueEl.textContent = formatMoney(row.format.value);
        break;
      case "signed-money": {
        const result = formatSignedMoney(row.format.value);
        valueEl.textContent = result.text;
        valueEl.classList.add(toneClass(result.tone));
        break;
      }
      case "magnitude-percent": {
        const result = formatMagnitudePercent(row.format.value);
        valueEl.textContent = result.text;
        valueEl.classList.add(toneClass(result.tone));
        break;
      }
      case "signed-number": {
        const result = formatSignedNumber(row.format.value, row.format.decimals);
        valueEl.textContent = result.text;
        valueEl.classList.add(toneClass(result.tone));
        break;
      }
      case "text":
        valueEl.textContent = row.format.value;
        break;
    }

    rowEl.appendChild(labelEl);
    rowEl.appendChild(valueEl);
    return rowEl;
  }

  dispose(): void {
    this.disposed = true;
    this.poller?.stop();
    this.chart?.dispose();
  }
}
